import type { ExternalProvider } from './externalAuth';

const nativeReturnHost = 'pre-jp-fx6.sparkdraw.cn';
const nativeReturnHosts = new Set([nativeReturnHost, 'forex-jp-pre-quantum.sparkdraw.cn']);

/** HTTPS App Links avoid browsers that reject non-HTTP URL protocols. */
export function externalAppReturnLink(provider: string, flowId: string, handoff: string, android: boolean): string | null {
  if (!['x', 'telegram', 'apple'].includes(provider) || !/^[a-f0-9]{64}$/.test(flowId)
    || !/^[A-Za-z0-9_-]{32,128}$/.test(handoff)) return null;
  const route = `//external-auth/return/${provider}?${new URLSearchParams({ flow_id: flowId, handoff })}`;
  return android
    ? `https://${nativeReturnHost}/auth/native/return/${provider}?${new URLSearchParams({ flow_id: flowId, handoff })}`
    : `forexmind:${route}`;
}

/** Web login returns are deliberately not claimed by the installed App. */
export function parseExternalAppReturnLink(raw: string): { path: string; provider: ExternalProvider; flowId: string; handoff: string } | null {
  try {
    const url = new URL(raw);
    if (url.username || url.password || url.port || url.hash) return null;
    const custom = url.protocol === 'forexmind:' && url.hostname === 'external-auth';
    const verified = url.protocol === 'https:' && nativeReturnHosts.has(url.hostname);
    const pattern = custom ? /^\/return\/(x|telegram|apple)$/ : verified ? /^\/auth\/native\/return\/(x|telegram|apple)$/ : null;
    const provider = pattern ? url.pathname.match(pattern)?.[1] as ExternalProvider | undefined : undefined;
    const flowId = url.searchParams.get('flow_id') || '';
    const handoff = url.searchParams.get('handoff') || '';
    if (!provider || [...url.searchParams].length !== 2
      || [...url.searchParams.keys()].some(key => !['flow_id', 'handoff'].includes(key))
      || !/^[a-f0-9]{64}$/.test(flowId) || !/^[A-Za-z0-9_-]{32,128}$/.test(handoff)) return null;
    return { path: `/auth/external/return/${provider}`, provider, flowId, handoff };
  } catch { return null; }
}

/** Recheck on resume as background browsers may suspend JavaScript timers. */
export function watchExternalAuthExpiry(expiresAt: number, onExpire: () => void): () => void {
  let finished = false;
  let timer: ReturnType<typeof setTimeout>;
  const stop = () => {
    finished = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', check);
    window.removeEventListener('focus', check);
  };
  const check = () => {
    if (finished || Date.now() < expiresAt) return;
    stop();
    onExpire();
  };
  document.addEventListener('visibilitychange', check);
  window.addEventListener('focus', check);
  timer = setTimeout(check, Math.max(0, expiresAt - Date.now()));
  check();
  return stop;
}
